import { DOCUMENT, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { ApiService } from '@core/services/api.service';
import { AuctionDownloadedService } from '@core/services/auction-downloaded.service';
import { AuthService } from '@core/services/auth.service';
import { ModalUtilsService } from '@core/services/modal-utils.service';
import { ApiServiceHolder } from '@core/utils/api-service-holder';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventService } from '@shared/services/event.service';
import { LanguageService } from '@shared/services/language.service';
import { MetaManagerService } from '@shared/services/meta-manager.service';
import { PublicGenericService } from '@shared/services/public-generic.service';
import { SearchService } from '@shared/services/search.service';
import { environment } from 'src/environments/environment';
import {
  Auction,
  AuctionView,
  buildAuction,
} from '../articles-manager/interfaces/auction.interface';
import { AuctionViewService } from '../articles-manager/services/auction-view.service';
import { ArticleOffersDialogComponent } from './components/dialogs/article-offers-dialog/article-offers-dialog.component';
import { BidSuccessDialogComponent } from './components/dialogs/bid-success-dialog/bid-success-dialog.component';
import { ContactsDialogComponent } from './components/dialogs/contacts-dialog/contacts-dialog.component';

import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from '@ngx-translate/core';
import { NoPaymentMethodDialogComponent } from '@shared/components/no-payment-method-dialog/no-payment-method-dialog.component';
import { SuggestedAuctionsComponent } from '@shared/components/suggested-auctions/suggested-auctions.component';
import { ImagePreloadService } from '@shared/services/image-preload.service';
import { PlatformService } from '@shared/services/platform-service.service';
import { RedirectService } from '@shared/services/redirect.service';
import { SEOService } from '@shared/services/seo.service';
import { NgxMaskDirective } from 'ngx-mask';
import { Subscription, timer } from 'rxjs';
import slugify from 'slugify';
import { SellerBrandingService } from '../seller/services/seller-branding.service';
import { TagsComponent } from '../shared/components/tags/tags.component';
import { ArticleBidComponent } from './components/article-bid/article-bid.component';
import { ArticleDescriptionComponent } from './components/article-description/article-description.component';
import { ArticleGalleryComponent } from './components/article-gallery/article-gallery.component';
import { ArticlePropertiesComponent } from './components/article-properties/article-properties.component';
import { DialogArticleType } from './interfaces/article-bid.interface';

@Component({
  selector: 'app-article',
  templateUrl: './article.component.html',
  styleUrls: ['./article.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    FaIconComponent,
    TranslatePipe,
    NgxMaskDirective,
    SuggestedAuctionsComponent,
    NoPaymentMethodDialogComponent,
    TagsComponent,
    ArticleGalleryComponent,
    ArticleBidComponent,
    ArticleDescriptionComponent,
    ArticlePropertiesComponent,
  ],
})
@UntilDestroy()
export class ArticleComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly searchService = inject(SearchService);
  private readonly modalUtils = inject(ModalUtilsService);
  private readonly auctionViewService = inject(AuctionViewService);
  private readonly auctionDownloadedService = inject(AuctionDownloadedService);
  private readonly route = inject(ActivatedRoute);
  private readonly seoService = inject(SEOService);
  private readonly redirectService = inject(RedirectService);
  private readonly languageService = inject(LanguageService);
  private readonly publicGenericService = inject(PublicGenericService);
  private readonly datePipe = inject(DatePipe);
  private readonly metaManagerService = inject(MetaManagerService);
  private readonly _api = inject(ApiService);
  private readonly eventService = inject(EventService);
  public readonly authService = inject(AuthService);
  private readonly branding = inject(SellerBrandingService);
  private readonly ngZone = inject(NgZone);
  private priceRanges: PriceRange[] = [];

  faEdit = faEdit;

  isLoaded = signal(false);
  id = signal<number | null>(null);
  isMemorabid = computed(() => this.selectedAuction()?.idAuctionType === 1);
  minimumOffer = signal<number | null>(null);
  slug = signal<string | null>(null);

  selectedAuction = signal<Auction>(buildAuction({}));

  sportIds = signal<string | null>(null);
  competitionIds = signal<string | null>(null);
  teamIds = signal<string | null>(null);
  isOwnerTenant = computed<boolean>(() => {
    return this.selectedAuction().idTenantEntity === 1;
  });

  title = computed(
    () =>
      this.selectedAuction().article.articleInfoList.find(
        (info) =>
          info.languageCode === this.languageService.userSelectedLanguage.code
      )?.title
  );

  memorabidAuctionSuggestions = signal<AuctionView[]>([]);
  marketplaceAuctionSuggestions = signal<AuctionView[]>([]);
  private static readonly suggestionsCache = new Map<
    number,
    CachedSuggestions
  >();

  get inPreEventStatus(): boolean {
    if (this.selectedAuction()) {
      return (
        this.selectedAuction().isPreEvent &&
        !this.selectedAuction().eventIsOpen &&
        !this.selectedAuction().isFinished
      );
    } else {
      return false;
    }
  }

  get isPreEventFinished(): boolean {
    return this.inPreEventStatus && !!this.selectedAuction().preEventCloseDate;
    // inPreEventStatus controlla già che l'asta abbia "isFinished" a false e l'evento non sia in corso
  }

  public timeDifferenceFromStart: number;
  private _refreshTimer: Subscription | null = null;
  private readonly _refreshTimerIntervalNotLogged = 20_000;
  private readonly _refreshTimerIntervalLogged = 2000;

  private readonly platformService = inject(PlatformService);
  private readonly imagePreloadService = inject(ImagePreloadService);

  private readonly _document = inject(DOCUMENT);
  private idleTasks: { type: 'idle' | 'timeout'; handle: number }[] = [];
  @ViewChild('suggestionsSentinel')
  private readonly suggestionsSentinel?: ElementRef<HTMLElement>;
  private suggestionsObserver: IntersectionObserver | null = null;
  private readonly pendingSuggestionAuctionId = signal<number | null>(null);
  private readonly suggestionsFetchStarted = signal(false);
  private suggestionsFallbackHandle: number | null = null;
  private readonly refreshAuctionWithinZone = () => {
    this.ngZone.run(() => this.updateAuction());
  };

  constructor() {
    effect((onDestroy) => {
      onDestroy(() => {
        this.metaManagerService.removeNoIndex();
        this.branding.clearBranding();
      });
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe((params: Params) => {
      const rawParam = params['idAuction'];
      if (typeof rawParam !== 'string') {
        // Ignore emissions without the param (can happen during route reuse/async guards)
        return;
      }
      const parts = rawParam.split('-');
      if (parts.length === 0 || parts[0].trim() === '') return;
      const numericId = Number(parts[0]);
      if (!Number.isFinite(numericId)) return;
      const slug = parts.slice(1).join('-');

      // Avoid unnecessary reload if same auction already selected
      if (this.id() === numericId && this.slug() === slug) return;

      this.id.set(numericId);
      this.slug.set(slug);
      this.isLoaded.set(false);
      this.memorabidAuctionSuggestions.set([]);
      this.marketplaceAuctionSuggestions.set([]);
      this.prepareSuggestions(numericId);
      this.loadAuctionDetail(numericId);

      this.authService
        .getUserLogged()
        .pipe(untilDestroyed(this))
        .subscribe(() => this.startTimer());
    });

    this.publicGenericService.getPriceRange().subscribe((priceRangeList) => {
      this.priceRanges = priceRangeList ?? [];
      if (this.priceRanges.length > 0) {
        this.updateAuction();
      }
    });
  }

  ngAfterViewInit(): void {
    this.setupSuggestionObserver();
  }

  onEdit(idAuction: number) {
    if (this.selectedAuction()?.idAuction) {
      if (this.selectedAuction().isCreatedByAdmin) {
        this.redirectService.redirect(`admin/articles/edit/${idAuction}`);
      } else if (this.authService.userLogged.idProfile === 1) {
        this.redirectService.redirect(
          `admin/seller-auctions/edit/${idAuction}`
        );
      } else {
        this.redirectService.redirect(`account/new-auction/${idAuction}`);
      }
    }
  }

  loadAuctionDetail(id: number, withDialog: DialogArticleType = undefined) {
    this.clearRefreshTimer();
    this.isLoaded.set(false);
    const lang = this.languageService.userSelectedLanguage.code;
    const langId = this.languageService.userSelectedLanguage.idLanguage;

    // Attempt to use preloaded cache first (when navigating from a card)
    const cached = this.auctionDownloadedService.getPreloadedAuction(
      id
    ) as unknown as Auction | undefined;
    if (cached) {
      // If cached object isn't full Auction, fall back to network fetch.
      // Heuristic: presence of article.articleInfoList implies full detail.
      if (cached.article?.articleInfoList?.length) {
        // Minimal path: treat cached as full detail and proceed asynchronously to refresh in background
        queueMicrotask(() => {
          // IMPORTANT: avoid opening success dialog twice.
          // When coming from a successful bid we call loadAuctionDetail with a dialog parameter.
          // We first paint cached data (fast) and then refresh from network. If we passed the
          // dialog flag here the success modal would open twice (once on cached data, once on fresh data).
          // So we suppress the dialog for the cached render and only allow it on the fresh fetch below.
          this.applyAuctionDetail(cached, lang, langId, undefined);
          // Refresh silently to ensure data freshness
          this.auctionViewService
            .getByPk(id)
            .pipe(untilDestroyed(this))
            .subscribe((fresh: Auction) => {
              if (fresh) {
                this.applyAuctionDetail(fresh, lang, langId, withDialog);
                this.auctionDownloadedService.cacheAuctionDetail(
                  fresh as unknown as AuctionView
                );
              }
            });
        });
        return;
      }
    }

    this.auctionViewService
      .getByPk(id)
      .pipe(untilDestroyed(this))
      .subscribe((dataR: Auction) => {
        this.auctionDownloadedService.cacheAuctionDetail(
          dataR as unknown as AuctionView
        );
        this.applyAuctionDetail(dataR, lang, langId, withDialog);
      });
  }

  // Extracted logic to reuse for cached + fresh detail
  private applyAuctionDetail(
    dataR: Auction,
    lang: string,
    langId: number,
    withDialog: DialogArticleType | undefined
  ) {
    if (!this.validateAndNormalizeAuction(dataR, lang)) {
      return;
    }

    this.prefetchPrimaryPhoto(dataR);
    this.isLoaded.set(true);

    const localizedAuction = this.buildLocalizedAuctionData(
      dataR,
      lang,
      langId
    );

    this.selectedAuction.set(localizedAuction);
    this.handleDialogFlow(withDialog, dataR);
    this.applySellerBranding(dataR);
    this.convertDates();
    this.setMinimumOffer();
    this.scheduleIdleTask(() => this.setArticleMetaData());
    this.maybeStartAuctionTimer(dataR);
  }

  setMinimumOffer() {
    let found = false;
    for (let i = 0; !found && i < this.priceRanges.length; i++) {
      const priceRange = this.priceRanges[i];
      const currentPrice =
        this.selectedAuction().currentMaxOfferedPrice != null
          ? this.selectedAuction().currentMaxOfferedPrice
          : this.selectedAuction().basePrice;
      if (
        currentPrice >= priceRange.fromPrice &&
        currentPrice <= priceRange.toPrice
      ) {
        if (this.selectedAuction().currentMaxOfferedPrice == null) {
          this.minimumOffer.set(currentPrice);
        } else {
          this.minimumOffer.set(currentPrice + priceRange.priceValue);
        }
        found = true;
      }
    }
  }

  private prepareSuggestions(idAuction: number) {
    const normalizedId = Number.isFinite(idAuction) ? idAuction : null;
    this.pendingSuggestionAuctionId.set(normalizedId);
    this.suggestionsFetchStarted.set(false);

    if (normalizedId !== null) {
      const cached = ArticleComponent.suggestionsCache.get(normalizedId);
      if (cached) {
        this.memorabidAuctionSuggestions.set([...cached.memorabid]);
        this.marketplaceAuctionSuggestions.set([...cached.marketplace]);
        this.scheduleIdleTask(() => this.setArticleMetaData());
      } else {
        this.memorabidAuctionSuggestions.set([]);
        this.marketplaceAuctionSuggestions.set([]);
      }
    } else {
      this.memorabidAuctionSuggestions.set([]);
      this.marketplaceAuctionSuggestions.set([]);
    }

    this.scheduleSuggestionsFallback();

    const win = this._document.defaultView as Window | null;
    const supportsIO = !!(win && 'IntersectionObserver' in win);
    if (!supportsIO) {
      this.scheduleIdleTask(() => this.fetchSuggestions());
      return;
    }

    // Wait for the view to stabilise to ensure sentinel exists before observing
    this.scheduleIdleTask(() => this.setupSuggestionObserver());
  }

  private fetchSuggestions(): void {
    const auctionId = this.pendingSuggestionAuctionId();
    if (this.suggestionsFetchStarted() || !auctionId) {
      return;
    }

    this.suggestionsFetchStarted.set(true);
    this.clearSuggestionsFallback();
    this.searchService
      .getSuggested(
        this.languageService.userSelectedLanguage.idLanguage,
        auctionId
      )
      .pipe(untilDestroyed(this))
      .subscribe((suggested) => {
        this.ngZone.run(() => {
          const memoraBid = [...(suggested.memoraBid ?? [])];
          const marketPlace = [...(suggested.marketPlace ?? [])];
          this.memorabidAuctionSuggestions.set(memoraBid);
          this.marketplaceAuctionSuggestions.set(marketPlace);
          ArticleComponent.suggestionsCache.set(auctionId, {
            memorabid: memoraBid,
            marketplace: marketPlace,
          });
          this.scheduleIdleTask(() => this.setArticleMetaData());
        });
      });
  }

  private setupSuggestionObserver(): void {
    const host = this.suggestionsSentinel?.nativeElement;
    const win = this._document.defaultView as
      | (Window & {
          IntersectionObserver?: typeof IntersectionObserver;
        })
      | null;
    if (!host || !win || !win.IntersectionObserver) {
      return;
    }

    if (!this.suggestionsObserver) {
      const ObserverCtor = win.IntersectionObserver;
      if (!ObserverCtor) {
        return;
      }
      this.suggestionsObserver = new ObserverCtor(
        (entries) => {
          for (const entry of entries) {
            console.log(entry, entry.isIntersecting);
            if (entry.isIntersecting) {
              this.fetchSuggestions();
              this.suggestionsObserver?.disconnect();
              break;
            }
          }
        },
        {
          rootMargin: '0px 0px 200px 0px',
          threshold: 0,
        }
      );
    } else {
      this.suggestionsObserver.disconnect();
    }

    this.suggestionsObserver.observe(host);
  }

  getHistory() {
    this.auctionViewService
      .getHistory(this.selectedAuction().idAuction)
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        if (response.totalBids > 0) {
          const ref = this.modalUtils.openModal(ArticleOffersDialogComponent, {
            size: 'lg',
            keyboard: false,
          });
          ref.componentInstance.auctionBidInfo.set(response);
          ref.componentInstance.idAuction.set(this.selectedAuction().idAuction);
          ref.result.then(
            () => {
              this.updateAuction();
            },
            () => {
              this.updateAuction();
            }
          );
        }
      });
  }

  reloadAuctionOnBidSuccess(modal: DialogArticleType) {
    this.loadAuctionDetail(this.id(), modal);
  }

  deleteAutoRebid(data: { idAutoRebid: number }) {
    this._api
      .request(ApiServiceHolder.auction.deleteAutoRebid(data.idAutoRebid))
      .subscribe(() => {
        this.loadAuctionDetail(this.id());
      });
  }

  contact() {
    if (this.authService.isAuthenticated()) {
      const ref = this.modalUtils.openModal(ContactsDialogComponent, {
        size: 'lg',
        keyboard: false,
      });
      ref.componentInstance.isMemorabid = this.isMemorabid();
      ref.componentInstance.inPreEventStatus = this.inPreEventStatus;
      ref.componentInstance.idAuction = this.id();
    }
  }

  ngOnDestroy() {
    this.metaManagerService.updateTitleAndMeta('', '', '', '');
    this.clearRefreshTimer();
    this.suggestionsObserver?.disconnect();
    this.suggestionsObserver = null;
    this.pendingSuggestionAuctionId.set(null);
    this.suggestionsFetchStarted.set(false);
    const win = this._document.defaultView as
      | (Window & {
          cancelIdleCallback?: (handle: number) => void;
        })
      | null;
    if (win) {
      for (const task of this.idleTasks) {
        if (task.type === 'idle' && win.cancelIdleCallback) {
          win.cancelIdleCallback(task.handle);
        } else if (task.type === 'timeout') {
          win.clearTimeout(task.handle);
        }
      }
      if (this.suggestionsFallbackHandle !== null) {
        win.clearTimeout(this.suggestionsFallbackHandle);
      }
    }
    this.idleTasks = [];
    this.suggestionsFallbackHandle = null;
  }

  stopRefresh() {
    this.clearRefreshTimer();
    this.updateAuction();
  }

  startTimer() {
    if (this.selectedAuction().isFinished || this.selectedAuction().buyItNow) {
      return;
    }

    this.platformService.run({
      browser: () => {
        if (this._refreshTimer) {
          this._refreshTimer.unsubscribe();
        }
        this.ngZone.runOutsideAngular(() => {
          this._refreshTimer = timer(
            0,
            this.authService.isAuthenticated()
              ? this._refreshTimerIntervalLogged
              : this._refreshTimerIntervalNotLogged
          )
            .pipe(untilDestroyed(this))
            .subscribe(this.refreshAuctionWithinZone);
        });
      },
    });
  }

  updateAuction() {
    if (!this.selectedAuction() || this.selectedAuction().idAuction === -1) {
      return null;
    }

    this.eventService
      .getAuctionPriceInfo(this.selectedAuction().idAuction)
      .pipe(untilDestroyed(this))
      .subscribe((dataR) => {
        this.selectedAuction.update((auction) => ({
          ...auction,
          ...dataR,
        }));

        // Set page as not crawlable if auction is not visible (on update)
        if (!this.selectedAuction().isVisible) {
          this.metaManagerService.setNoIndex();
        } else {
          this.metaManagerService.removeNoIndex();
        }

        this.convertDates();
        this.setMinimumOffer();

        if (this.selectedAuction().isFinished) {
          this.clearRefreshTimer();
        }
      });
  }

  convertDates() {
    const startDateUTC = new Date(this.selectedAuction().startDate + 'Z');
    let newEndDate = new Date();

    if (this.selectedAuction().endDateView == null) {
      // add duration to start date
      newEndDate = new Date(
        startDateUTC.getTime() + this.selectedAuction().durationMinutes * 60000
      );
    } else {
      newEndDate = new Date(this.selectedAuction().endDateView + ':00Z');
    }

    this.selectedAuction.update((auction) => ({
      ...auction,
      startDateView: this.datePipe.transform(startDateUTC, 'yyyy-MM-ddTHH:mm'),
      endDateView: this.datePipe.transform(newEndDate, 'yyyy-MM-ddTHH:mm'),
    }));
  }

  private clearRefreshTimer(): void {
    if (this._refreshTimer) {
      this._refreshTimer.unsubscribe();
      this._refreshTimer = null;
    }
  }

  private setArticleMetaData(): void {
    const langCode = this.languageService.userSelectedLanguage.code;
    // Determine a robust title with fallbacks
    const getFallbackTitle = (): string => {
      // 1. Try language-specific info
      const langInfo = this.selectedAuction().article.articleInfoList.find(
        (info) => info.languageCode === langCode
      );
      if (langInfo?.title) return langInfo.title;
      // 2. Any info list entry
      const firstInfo = this.selectedAuction().article.articleInfoList[0];
      if (firstInfo?.title) return firstInfo.title;
      // 3. Use slug (part after id)
      if (this.slug()) return this.slug();
      // 4. Generic fallback
      return 'Auction';
    };

    const articleTitle = getFallbackTitle();
    let articlePhoto = '';
    let articleDescription = '';

    if (
      this.selectedAuction().article.articleInfoList.length > 0 &&
      this.selectedAuction().article.articleInfoList[0]
    ) {
      // Keep original description logic, but title already resolved with fallback
      articleDescription =
        this.selectedAuction().article.articleInfoList[0].description.replace(
          /<[^>]+>/g,
          ''
        );
    }

    if (this.selectedAuction().article.articlePhotoList.length > 0) {
      articlePhoto =
        this.selectedAuction().article.articlePhotoList[0].fullPath;
    }

    this.metaManagerService.updateTitleAndMeta(
      articleTitle,
      articleDescription,
      environment.APPOrigin +
        '/' +
        this.languageService.userSelectedLanguage.code +
        '/auction/' +
        this.selectedAuction().idAuction +
        '-' +
        this.slug(),
      articlePhoto
    );

    const idPrefix =
      this.languageService.userSelectedLanguage.code === 'it'
        ? ''
        : this.languageService.userSelectedLanguage.code;

    const getSellerInfo = (idTenantEntity: number) => {
      if (idTenantEntity !== 1) {
        return {
          '@type': 'Organization',
          name: this.selectedAuction().entitySellerName || 'Ridyamo',
          url: `${environment.canonicalUrl}${this.languageService.userSelectedLanguage.code}/sellers/${idTenantEntity}`,
        };
      }
      return {
        '@type': 'Organization',
        name: 'Memorabid',
        url: `${environment.canonicalUrl}${this.languageService.userSelectedLanguage.code}`,
      };
    };

    const buildJsonLd = (article: AuctionView) => {
      const relatedTitle =
        article.title ||
        this.selectedAuction().article.articleInfoList[0]?.title ||
        this.slug() ||
        'Auction';
      return {
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': `${article.idAuction}`,
        id: `${idPrefix}${article.idAuction}`,
        name: relatedTitle,
        image: [article.articlePhoto.fullPath],
        offers: {
          '@type': 'Offer',
          url: `${environment.canonicalUrl}${this.languageService.userSelectedLanguage.code}/auction/${article.slug}`,
          priceCurrency: 'EUR',
          price: Number(
            (article.currentMaxOfferedPrice ?? article.basePrice).toFixed(2)
          ),
          image: article.articlePhoto.fullPath,
          seller: {
            '@type': 'Organization',
            name: 'Memorabid',
            url: `${environment.canonicalUrl}${this.languageService.userSelectedLanguage.code}`,
          },
        },
      };
    };

    const related = [
      ...this.memorabidAuctionSuggestions().map(buildJsonLd),
      ...this.marketplaceAuctionSuggestions().map(buildJsonLd),
    ];

    const jsonLdPayload = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': `${this.selectedAuction().idAuction}`,
      id: `${idPrefix}${this.selectedAuction().idAuction}`,
      name: articleTitle,
      image: this.selectedAuction().article.articlePhotoList.map(
        (photo) => photo.fullPath
      ),
      description: articleDescription,
      keywords: this.selectedAuction().article.articleTagList.map(
        (tag) =>
          `${environment.canonicalUrl}${this.languageService.userSelectedLanguage.code}/tags/${tag.description}`
      ),
      isRelatedTo: related,
      offers: {
        '@type': 'Offer',
        url: `${environment.canonicalUrl}${
          this.languageService.userSelectedLanguage.code
        }/auction/${this.selectedAuction().idAuction}-${this.slug()}`,
        priceCurrency: 'EUR',
        price: Number(
          (
            this.selectedAuction().currentMaxOfferedPrice ??
            this.selectedAuction().basePrice
          ).toFixed(2)
        ),
        availability: 'https://schema.org/InStock',
        image: articlePhoto,
        seller: getSellerInfo(this.selectedAuction().idTenantEntity ?? 1),
      },
    };

    const existingScript = this._document.getElementById(
      'json-ld-auction'
    ) as HTMLScriptElement | null;

    const newScript = this._document.createElement('script');
    newScript.type = 'application/ld+json';
    newScript.id = 'json-ld-auction';
    newScript.text = JSON.stringify(jsonLdPayload, null, 2);

    const head = this._document.head;
    if (existingScript) {
      head.replaceChild(newScript, existingScript);
    } else {
      head.appendChild(newScript);
    }
  }

  private scheduleIdleTask(cb: () => void, timeout = 500): void {
    const win = this._document.defaultView as
      | (Window & {
          requestIdleCallback?: (
            callback: IdleRequestCallback,
            options?: IdleRequestOptions
          ) => number;
          cancelIdleCallback?: (handle: number) => void;
        })
      | null;

    if (!win) {
      setTimeout(cb, 0);
      return;
    }

    const idleCallback = win.requestIdleCallback;

    if (idleCallback) {
      const handle = idleCallback(
        () => {
          this.removeIdleHandle(handle);
          cb();
        },
        { timeout }
      );
      this.idleTasks.push({ type: 'idle', handle });
    } else {
      const handle = win.setTimeout(() => {
        this.removeIdleHandle(handle);
        cb();
      }, 0);
      this.idleTasks.push({ type: 'timeout', handle });
    }
  }

  private removeIdleHandle(handle: number): void {
    const index = this.idleTasks.findIndex((entry) => entry.handle === handle);
    if (index !== -1) {
      this.idleTasks.splice(index, 1);
    }
  }

  private scheduleSuggestionsFallback(delayMs = 4000): void {
    const win = this._document.defaultView;
    if (!win || !this.pendingSuggestionAuctionId()) {
      return;
    }

    this.clearSuggestionsFallback();
    this.ngZone.runOutsideAngular(() => {
      this.suggestionsFallbackHandle = win.setTimeout(() => {
        this.suggestionsFallbackHandle = null;
        this.ngZone.run(() => this.fetchSuggestions());
      }, delayMs);
    });
  }

  private clearSuggestionsFallback(): void {
    const win = this._document.defaultView;
    if (this.suggestionsFallbackHandle !== null && win) {
      win.clearTimeout(this.suggestionsFallbackHandle);
      this.suggestionsFallbackHandle = null;
    } else if (this.suggestionsFallbackHandle !== null) {
      this.suggestionsFallbackHandle = null;
    }
  }

  private validateAndNormalizeAuction(auction: Auction, lang: string): boolean {
    if (!auction || auction.idAuction === -1) {
      this.redirectService.redirect('home');
      return false;
    }

    if (!auction.isVisible) {
      this.metaManagerService.setNoIndex();
    } else {
      this.metaManagerService.removeNoIndex();
    }

    const localizedSlug = this.resolveLocalizedSlug(auction, lang);
    const hasDifferentSlug = this.slug() !== localizedSlug;
    const hasDifferentId = this.id() !== auction.idAuction;
    if (hasDifferentSlug || hasDifferentId) {
      this.redirectService.redirect(
        `auction/${auction.idAuction}-${localizedSlug}`,
        301
      );
      return false;
    }

    return true;
  }

  private resolveLocalizedSlug(auction: Auction, lang: string): string {
    const localizedInfo = auction.article.articleInfoList.find(
      (info) => info.languageCode === lang
    );

    if (localizedInfo?.slug) {
      return localizedInfo.slug;
    }

    return slugify(localizedInfo?.title ?? '', { lower: true, strict: true });
  }

  private prefetchPrimaryPhoto(auction: Auction): void {
    const firstPhoto = auction.article.articlePhotoList[0];
    if (!firstPhoto) {
      return;
    }

    this.scheduleIdleTask(() =>
      this.imagePreloadService
        .preloadImage(`${firstPhoto.fullPath}?w=600`)
        .catch(() => {})
    );
  }

  private buildLocalizedAuctionData(
    auction: Auction,
    lang: string,
    langId: number
  ): Auction {
    const data = { ...auction, article: { ...auction.article } };

    const infoList = auction.article.articleInfoList.find(
      (info) => info.languageCode === lang
    );

    data.article.articleTagList = auction.article.articleTagList.map((tag) => ({
      ...tag,
      tagTranslatedList: tag.tagTranslatedList.filter(
        (translation) => translation.idLanguage === langId
      ),
    }));

    const campaignTitle = auction.campaignTitleList.find(
      (title) => title.idLanguage === langId
    );
    const promoterTitle = auction.promoterTitleList.find(
      (title) => title.idLanguage === langId
    );

    data.article.articleInfoList = infoList ? [infoList] : [];
    data.campaignTitleList = campaignTitle ? [campaignTitle] : [];
    data.promoterTitleList = promoterTitle ? [promoterTitle] : [];

    this.updateCanonicalUrls(auction, lang);
    this.updateTaxonomies(auction);

    return data;
  }

  private updateCanonicalUrls(auction: Auction, lang: string): void {
    const activeSlug = this.slug() ?? this.resolveLocalizedSlug(auction, lang);
    const canonicalBase = `${environment.canonicalUrl}${lang}/auction/${auction.idAuction}-${activeSlug}`;
    const italianSlug =
      auction.article.articleInfoList.find(
        (info) => info.languageCode.toUpperCase() === 'IT'
      )?.slug ?? activeSlug;
    const englishSlug =
      auction.article.articleInfoList.find(
        (info) => info.languageCode.toUpperCase() === 'EN'
      )?.slug ?? activeSlug;

    this.seoService.updateCanonicalUrl(canonicalBase, {
      it: `${environment.canonicalUrl}it/auction/${auction.idAuction}-${italianSlug}`,
      en: `${environment.canonicalUrl}en/auction/${auction.idAuction}-${englishSlug}`,
    });
  }

  private updateTaxonomies(auction: Auction): void {
    this.sportIds.set(auction.article.sportTranslationKey ?? '');
    this.competitionIds.set(auction.article.competitionTranslationKey ?? '');
    this.teamIds.set(auction.article.teamTranslationKey ?? '');
  }

  private handleDialogFlow(
    withDialog: DialogArticleType | undefined,
    auction: Auction
  ): void {
    if (withDialog === 'BID') {
      const ref = this.modalUtils.openModal(BidSuccessDialogComponent, {
        size: 'md',
        keyboard: false,
      });
      ref.componentInstance.selectedAuction = auction;
      return;
    }

    if (withDialog === 'BUY_NOW') {
      const ref = this.modalUtils.openModal(BidSuccessDialogComponent, {
        size: 'md',
        keyboard: false,
      });
      ref.componentInstance.isBuyNow = true;
      ref.componentInstance.selectedAuction = auction;
    }
  }

  private applySellerBranding(auction: Auction): void {
    const sellerId =
      auction.idTenantEntity !== 1 ? auction.idTenantEntity : null;

    if (sellerId) {
      this.branding.applyBrandingFromId(sellerId);
      return;
    }

    this.branding.clearBranding();
  }

  private maybeStartAuctionTimer(auction: Auction): void {
    if (!auction.startDate) {
      return;
    }

    const startDate = new Date(`${auction.startDate}Z`);
    if (Number.isNaN(startDate.getTime())) {
      return;
    }

    const timeFromStart = Date.now() - startDate.getTime();
    if (timeFromStart > 0 && !auction.isFinished) {
      this.startTimer();
    }
  }
}

interface PriceRange {
  fromPrice: number;
  toPrice: number;
  priceValue: number;
}

interface CachedSuggestions {
  memorabid: AuctionView[];
  marketplace: AuctionView[];
}
